import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { Map as MapIcon } from '@mui/icons-material';
import LazyStaticMap from './maps/LazyStaticMap';
import UniversalMap from './maps/UniversalMap';
import { UniversalMapEntry } from './maps/MapInterface';
import { calculateCenter, calculateOptimalZoom } from './maps/utils/staticMapUtils';

interface PublicMemory {
  id: number;
  title: string;
  description?: string;
  public_slug: string;
  entry_date: string;
  location_name?: string;
  website_url?: string;
  latitude?: number | string;
  longitude?: number | string;
  thumbnail_url?: string;
  featured: boolean;
  isDogFriendly?: boolean;
}

interface PublicProfileStaticMapProps {
  memories: PublicMemory[];
  onMemoryClick?: (memory: PublicMemory) => void;
}

// Extended interface for our specific needs
interface ExtendedMapEntry extends UniversalMapEntry {
  thumbnailUrl?: string;
  featured: boolean;
  onClick: () => void;
}

const PublicProfileStaticMap: React.FC<PublicProfileStaticMapProps> = ({ 
  memories, 
  onMemoryClick 
}) => {
  const [mapEntries, setMapEntries] = useState<ExtendedMapEntry[]>([]);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Responsive map dimensions
  const getMapDimensions = () => {
    if (isMobile) {
      return { width: 1200, height: 250 }; // Keep wide image for quality, adjust display height
    } else if (isTablet) {
      return { width: 1200, height: 350 };
    } else {
      return { width: 1200, height: 400 };
    }
  };

  const { width: MAP_WIDTH, height: MAP_HEIGHT } = getMapDimensions();

  useEffect(() => {
    // Convert memories to Universal Map entries format
    const validEntries = memories
      .filter(memory => {
        // Check for valid coordinate data - handle both string and number types
        const hasLat = memory.latitude !== null && memory.latitude !== undefined && memory.latitude !== '';
        const hasLng = memory.longitude !== null && memory.longitude !== undefined && memory.longitude !== '';
        return hasLat && hasLng;
      })
      .map(memory => {
        // Convert string coordinates to numbers with better error handling
        let lat: number;
        let lng: number;
        
        if (typeof memory.latitude === 'string') {
          lat = parseFloat(memory.latitude);
        } else if (typeof memory.latitude === 'number') {
          lat = memory.latitude;
        } else {
          console.warn('Invalid latitude type for memory:', memory.title, typeof memory.latitude);
          return null;
        }
        
        if (typeof memory.longitude === 'string') {
          lng = parseFloat(memory.longitude);
        } else if (typeof memory.longitude === 'number') {
          lng = memory.longitude;
        } else {
          console.warn('Invalid longitude type for memory:', memory.title, typeof memory.longitude);
          return null;
        }
        
        // Validate the coordinates are valid numbers and within bounds
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.warn('Invalid coordinates for memory:', memory.title, { lat, lng });
          return null;
        }

        return {
          id: memory.id.toString(),
          title: memory.title,
          location: { lat, lng },
          locationName: memory.location_name,
          content: memory.description || '',
          date: memory.entry_date,
          thumbnailUrl: memory.thumbnail_url,
          featured: memory.featured,
          onClick: () => {
            // Only allow clicks if memory has a public slug
            if (memory.public_slug && onMemoryClick) {
              onMemoryClick(memory);
            }
          }
        } as ExtendedMapEntry;
      })
      .filter((entry): entry is ExtendedMapEntry => entry !== null)
      .filter(entry => {
        // Find the original memory to check if it has a public slug
        const originalMemory = memories.find(m => m.id.toString() === entry.id);
        return originalMemory?.public_slug != null;
      });
    
    setMapEntries(validEntries);
  }, [memories, onMemoryClick]);

  const handleMapError = (error: Error) => {
    console.error('Static map error:', error);
    setMapError(error.message);
  };

  const handleShowInteractiveMap = () => {
    setShowInteractiveMap(true);
  };

  // Don't render if no valid entries
  if (mapEntries.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No locations available to display on map
        </Typography>
      </Paper>
    );
  }

  // Calculate optimal map parameters
  const center = calculateCenter(mapEntries);
  const zoom = calculateOptimalZoom(mapEntries, MAP_WIDTH, MAP_HEIGHT);

  // Validate center and zoom before rendering
  if (!center || isNaN(center.lat) || isNaN(center.lng) || isNaN(zoom)) {
    console.error('Invalid map parameters:', { center, zoom, entries: mapEntries });
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">Unable to display map - invalid location data</Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 0, 
        borderRadius: 2, 
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {!showInteractiveMap ? (
        // Static Map View
        <Box sx={{ 
          position: 'relative',
          height: { xs: 250, sm: 350, md: 400 }, // Responsive height
          width: '100%',
          bgcolor: '#f5f5f5', // Light background in case image doesn't fill
          borderRadius: 1,
          overflow: 'hidden',
          '& img': {
            // Ensure the image always fills the container and scales responsively
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }
        }}>
          <LazyStaticMap
            entries={mapEntries}
            center={center}
            zoom={zoom}
            width={MAP_WIDTH}
            height={MAP_HEIGHT}
            showMarkers={true}
            onError={handleMapError}
            alt={`Travel map showing ${mapEntries.length} locations`}
            rootMargin="100px"
            threshold={0.1}
            style={{
              width: '100%',
              height: '100%',
              display: 'block'
            }}
          />
          
          {/* Interactive Map Button Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<MapIcon />}
              onClick={handleShowInteractiveMap}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                }
              }}
            >
              Interactive Map
            </Button>
          </Box>

          {/* Location count badge */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              typography: 'body2'
            }}
          >
            {mapEntries.length} location{mapEntries.length !== 1 ? 's' : ''}
          </Box>

          {/* Error overlay */}
          {mapError && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                zIndex: 2
              }}
            >
              <Typography color="error" gutterBottom>
                Failed to load map
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleShowInteractiveMap}
              >
                Try Interactive Map
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        // Interactive Map View
        <Box sx={{ height: MAP_HEIGHT, width: '100%' }}>
          <UniversalMap
            entries={mapEntries.map(entry => ({
              id: entry.id,
              title: entry.title,
              location: entry.location,
              locationName: entry.locationName,
              content: entry.content,
              date: entry.date
            }))}
            center={center}
            zoom={zoom}
            onMarkerClick={(entry: UniversalMapEntry) => {
              const extendedEntry = mapEntries.find(e => e.id === entry.id);
              if (extendedEntry?.onClick) {
                extendedEntry.onClick();
              }
            }}
            style={{ height: '100%', width: '100%' }}
          />
          
          {/* Back to Static Button */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowInteractiveMap(false)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                }
              }}
            >
              Static View
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PublicProfileStaticMap;